import { apiClient as api } from "./httpClient";

// Cadastro de fluxos de trabalho da entidade e das suas versões. Só configuração:
// aplicar um fluxo a um paciente (instância) é outra feature.
// Regra central: versão publicada é IMUTÁVEL — para mudar, clona-se em uma versão nova.

export type WorkflowTargetKind = "patient" | "professional" | "entity" | "none";
export type WorkflowVersionStatus = "draft" | "published" | "retired";
export type WorkflowStepKind = "activity" | "decision" | "milestone";
export type WorkflowDueAnchor = "workflow_start" | "previous_step" | "none";
export type WorkflowDueUnit = "business_hours" | "days" | "weeks" | "months" | "years";

export const TARGET_KIND_LABELS: Record<WorkflowTargetKind, string> = {
  patient: "Paciente",
  professional: "Profissional",
  entity: "Entidade",
  none: "Sem alvo",
};

export const VERSION_STATUS_LABELS: Record<WorkflowVersionStatus, string> = {
  draft: "Rascunho",
  published: "Publicada",
  retired: "Encerrada",
};

export const VERSION_STATUS_COLORS: Record<WorkflowVersionStatus, string> = {
  draft: "#fd7e14",
  published: "#28a745",
  retired: "#6c757d",
};

export const STEP_KIND_LABELS: Record<WorkflowStepKind, string> = {
  activity: "Atividade",
  decision: "Decisão",
  milestone: "Marco",
};

/** Horas ÚTEIS contam pelo expediente da entidade — não são horas corridas. */
export const DUE_UNIT_LABELS: Record<WorkflowDueUnit, string> = {
  business_hours: "horas úteis",
  days: "dias",
  weeks: "semanas",
  months: "meses",
  years: "anos",
};

export const DUE_ANCHOR_LABELS: Record<WorkflowDueAnchor, string> = {
  workflow_start: "Início do fluxo",
  previous_step: "Etapa anterior",
  none: "Sem prazo",
};

/** Verificadores concluem a etapa sozinhos a partir de evidência do sistema; vazio = só manual. */
export const CHECKER_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Somente manual" },
  { value: "first_attendance_done", label: "Primeiro atendimento realizado" },
  { value: "evaluation_done", label: "Avaliação realizada" },
  { value: "diagnosis_registered", label: "Diagnóstico registrado" },
  { value: "therapy_plan_active", label: "Plano terapêutico vigente" },
  { value: "outcome_registered", label: "Desfecho registrado" },
];

/** Sugestões de setor — texto livre, a clínica pode usar outro. */
export const CATEGORY_SUGGESTIONS = [
  "Clínico",
  "Administrativo",
  "Financeiro",
  "Comercial",
  "Pessoas",
];

export interface WorkflowListItem {
  id: string;
  name: string;
  purpose: string | null;
  category: string | null;
  /** Guarda-chuva ao qual o fluxo pertence ("Ciclo de vida do paciente"). */
  groupName: string | null;
  targetKind: WorkflowTargetKind;
  videoUrl: string | null;
  active: boolean;
  versionCount: number;
  currentVersionNumber: number | null;
  currentVersionId: string | null;
  draftVersionId: string | null;
  draftVersionNumber: number | null;
  visibilityProfileIds: string[];
}

/** Documento de apoio do fluxo (modelo, checklist, manual). */
export interface WorkflowAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

/** Limites da API para anexo — espelhados aqui só para avisar antes de subir o arquivo. */
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_MAX_COUNT = 20;
export const ATTACHMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx";

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export interface WorkflowVersionSummary {
  id: string;
  versionNumber: number;
  status: WorkflowVersionStatus;
  notes: string | null;
  stepCount: number;
  clonedFromVersionId: string | null;
  clonedFromVersionNumber: number | null;
  createdAt: string;
  publishedAt: string | null;
  retiredAt: string | null;
}

export interface WorkflowStepOption {
  optionKey: string;
  label: string;
  order: number;
}

export interface WorkflowStep {
  stepKey: string;
  kind: WorkflowStepKind;
  name: string;
  description: string | null;
  checker: string | null;
  dueAnchor: WorkflowDueAnchor;
  dueAmount: number | null;
  dueUnit: WorkflowDueUnit;
  responsibleProfileId: string | null;
  required: boolean;
  order: number;
  options: WorkflowStepOption[];
}

/**
 * Ligação por step_key (chave estável). O destino é uma etapa deste fluxo (`toStepKey`), outro
 * fluxo de trabalho (`toWorkflowId`) ou nenhum — os dois nulos encerram o processo. `toStepKey` e
 * `toWorkflowId` são mutuamente exclusivos: o servidor recusa os dois preenchidos.
 */
export interface WorkflowTransition {
  fromStepKey: string;
  toStepKey: string | null;
  toWorkflowId: string | null;
  /** Nome resolvido pelo servidor na leitura; null quando o fluxo saiu do ar. */
  toWorkflowName: string | null;
  optionKey: string | null;
}

/** O nome do fluxo de destino é resolvido pelo servidor — na gravação ele não é enviado. */
export type WorkflowTransitionInput = Omit<WorkflowTransition, "toWorkflowName">;

export interface WorkflowVersionDetail {
  id: string;
  workflowId: string;
  workflowName: string;
  targetKind: WorkflowTargetKind;
  versionNumber: number;
  status: WorkflowVersionStatus;
  notes: string | null;
  publishedAt: string | null;
  steps: WorkflowStep[];
  transitions: WorkflowTransition[];
}

/** Perfil do tenant, para o campo "responsável" da etapa. */
export interface WorkflowProfileOption {
  id: string;
  name: string;
}

export interface SaveWorkflowPayload {
  name: string;
  purpose: string | null;
  category: string | null;
  groupName: string | null;
  targetKind: WorkflowTargetKind;
  videoUrl: string | null;
  visibilityProfileIds: string[];
}

export interface SaveWorkflowDesignPayload {
  notes: string | null;
  steps: WorkflowStep[];
  transitions: WorkflowTransitionInput[];
}

export const workflowsService = {
  getByEntity: async (entityId: string, includeInactive = true): Promise<WorkflowListItem[]> => {
    const response = await api.get(`/workflows/by-entity/${entityId}?includeInactive=${includeInactive}`);
    return response.data;
  },

  getVersions: async (workflowId: string): Promise<WorkflowVersionSummary[]> => {
    const response = await api.get(`/workflows/${workflowId}/versions`);
    return response.data;
  },

  /** Desenho completo de uma versão (etapas, opções e ligações). */
  getVersion: async (versionId: string): Promise<WorkflowVersionDetail> => {
    const response = await api.get(`/workflows/versions/${versionId}`);
    return response.data;
  },

  /** Perfis do tenant para o campo "responsável" da etapa. */
  getProfiles: async (): Promise<WorkflowProfileOption[]> => {
    const response = await api.get("/workflows/profiles");
    return response.data;
  },

  /** Cria o fluxo já com a versão 1 em rascunho. */
  create: async (entityId: string, payload: SaveWorkflowPayload): Promise<string> => {
    const response = await api.post("/workflows", { entityId, ...payload });
    return response.data.id;
  },

  update: async (workflowId: string, payload: SaveWorkflowPayload): Promise<void> => {
    await api.put(`/workflows/${workflowId}`, payload);
  },

  setActive: async (workflowId: string, active: boolean): Promise<void> => {
    await api.patch(`/workflows/${workflowId}/active`, { active });
  },

  /** Só remove fluxo que nunca teve versão publicada — os demais a API recusa com mensagem. */
  remove: async (workflowId: string): Promise<void> => {
    await api.delete(`/workflows/${workflowId}`);
  },

  /** Abre um rascunho: cópia da versão informada ou desenho em branco (cloneFromVersionId null). */
  createVersion: async (
    workflowId: string,
    cloneFromVersionId: string | null,
    notes: string | null
  ): Promise<string> => {
    const response = await api.post(`/workflows/${workflowId}/versions`, { cloneFromVersionId, notes });
    return response.data.id;
  },

  saveDesign: async (versionId: string, payload: SaveWorkflowDesignPayload): Promise<void> => {
    await api.put(`/workflows/versions/${versionId}/design`, payload);
  },

  publishVersion: async (versionId: string): Promise<void> => {
    await api.post(`/workflows/versions/${versionId}/publish`);
  },

  unpublishVersion: async (versionId: string): Promise<void> => {
    await api.post(`/workflows/versions/${versionId}/unpublish`);
  },

  retireVersion: async (versionId: string): Promise<void> => {
    await api.post(`/workflows/versions/${versionId}/retire`);
  },

  removeVersion: async (versionId: string): Promise<void> => {
    await api.delete(`/workflows/versions/${versionId}`);
  },

  getAttachments: async (workflowId: string): Promise<WorkflowAttachment[]> => {
    const response = await api.get(`/workflows/${workflowId}/attachments`);
    return response.data;
  },

  // O apiClient manda Content-Type: application/json por padrão e ele VAZA para o FormData
  // (a API responde 415). Anulando o header, o browser define o multipart/form-data com boundary.
  uploadAttachment: async (workflowId: string, file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file);
    const response = await api.post(`/workflows/${workflowId}/attachments`, form, {
      headers: { "Content-Type": null },
    });
    return response.data.id as string;
  },

  removeAttachment: async (attachmentId: string): Promise<void> => {
    await api.delete(`/workflows/attachments/${attachmentId}`);
  },
};

// ===== Consulta (Playbook) — permissão workflow-catalog =====

export type WorkflowAccessReason = "visibility" | "step" | "admin";

export const ACCESS_REASON_LABELS: Record<WorkflowAccessReason, string> = {
  visibility: "Liberado para o seu perfil",
  step: "Você responde por uma etapa",
  admin: "Visível por ser administrador da entidade",
};

export interface WorkflowCatalogItem {
  id: string;
  name: string;
  purpose: string | null;
  category: string | null;
  groupName: string | null;
  targetKind: WorkflowTargetKind;
  versionNumber: number;
  stepCount: number;
  publishedAt: string | null;
  accessReason: WorkflowAccessReason;
}

export interface WorkflowCatalogPage {
  items: WorkflowCatalogItem[];
  categories: string[];
  groups: string[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface WorkflowCatalogDetail {
  id: string;
  name: string;
  purpose: string | null;
  category: string | null;
  groupName: string | null;
  targetKind: WorkflowTargetKind;
  versionNumber: number;
  publishedAt: string | null;
  /** URL já normalizada para embed pela API; null quando o fluxo não tem vídeo. */
  videoEmbedUrl: string | null;
  steps: WorkflowStep[];
  transitions: WorkflowTransition[];
  attachments: WorkflowAttachment[];
  /** Nome dos perfis responsáveis pelas etapas, por id — vem do detalhe porque a consulta
   *  não alcança o endpoint de perfis da configuração. */
  profileNames: Record<string, string>;
}

export interface WorkflowCatalogFilters {
  search?: string;
  category?: string;
  groupName?: string;
  pageNumber: number;
  pageSize: number;
}

export const workflowCatalogService = {
  getByEntity: async (
    entityId: string,
    filters: WorkflowCatalogFilters
  ): Promise<WorkflowCatalogPage> => {
    const response = await api.get(`/workflow-catalog/by-entity/${entityId}`, {
      params: {
        search: filters.search?.trim() || undefined,
        category: filters.category || undefined,
        groupName: filters.groupName || undefined,
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
      },
    });
    return response.data;
  },

  getById: async (workflowId: string): Promise<WorkflowCatalogDetail> => {
    const response = await api.get(`/workflow-catalog/${workflowId}`);
    return response.data;
  },

  downloadAttachment: async (attachmentId: string): Promise<Blob> => {
    const response = await api.get(`/workflow-catalog/attachments/${attachmentId}/file`, {
      responseType: "blob",
    });
    return response.data;
  },
};

/** Dispara o download de um blob com o nome original do arquivo. */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

/** Mensagem legível devolvida pela API (Result.Error.description); cai no texto padrão quando não vier. */
export const workflowErrorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { description?: string } } })?.response?.data?.description || fallback;
